/**
 * Support Tasks Page
 * Database-backed support team task management
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDebounce } from "../hooks/useDebounce";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  getAgents,
  getAgentTasks,
  getPastDueTasks,
  getUnassignedTasks,
  getUnassignedTaskCount,
  getSupportTaskCounts,
  type SupportTaskCountsByAgent,
  getAgentByUserId,
  getTaskById,
  updateTaskStatus,
  updateTaskPriority,
  addCustomerTag,
  removeCustomerTag,
  linkInteractionToTask,
  reassignTask,
  bulkReassignTasks,
  bulkSoftDeleteTasks,
  getUniqueTags,
  getResolutionTemplates,
  getLinkedInteractions,
  getActivityCountsForTasks,
  escalateTask,
  deescalateTask,
  markTaskViewed,
  updateTaskDueDate,
  updateTaskType,
  getChildTasks,
  getSupportTaskTabCounts,
  appendTaskNote,
  SupportTaskNotUpdatedError,
} from "../services/supportTasksDb.service";
import SupportAdminDashboard from "../components/supportTasks/SupportAdminDashboard";
import { ExternalTaskId } from "../components/supportTasks/ExternalTaskId";
import {
  buildSupportTaskInteraction,
  createInteraction,
} from "../services/interactions.service";
import type { InteractionOutcome } from "../types/interaction.types";
import type { InteractionMethod } from "../types/interaction.types";
import type {
  SupportAgent,
  SupportTask,
  SupportTaskType,
  TaskFilter,
  TaskResolutionData,
  TaskStatus,
  TaskTabKey,
  TaskTypeFilterValue,
  CustomerSnapshot,
} from "../types/supportTasks.types";
import {
  TASK_TYPE_LABELS,
  groupedTaskTypeOptions,
  taskTypeFilterOptions,
  TASK_STATUS_CONFIG,
  TASK_PRIORITY_CONFIG,
} from "../types/supportTasks.types";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../types/auth.types";
import {
  UNASSIGNED_FILTER_SENTINEL,
  ESCALATED_FILTER_SENTINEL,
  canViewEscalatedToMe,
  resolveAgentView,
  resolveDeepLinkView,
} from "./supportTasksAgentView";
// CSV import removed — tasks come via API only
import AgentManagementPanel from "../components/supportTasks/AgentManagementPanel";
import type { UUID } from "../types/common.types";
import CreateTaskModal from "../components/supportTasks/CreateTaskModal";
import {
  contactPickerCanSearch,
  contactPickerStatus,
} from "../utils/contactPickerStatus";
import {
  searchContacts,
  getContactByMembershipId,
} from "../services/contacts.service";
import type { Contact } from "../types/contact.types";
import ContactQuickView from "../components/contacts/ContactQuickView";
import { useToast } from "../components/ui/Toast";

/**
 * Check if a name is derived from an email address.
 * Catches full emails (user@domain.com) and email local parts (user123)
 * by comparing against the contact's known email.
 */
function isNameFromEmail(name: string, email?: string): boolean {
  if (!name) {
    return false;
  }
  // Full email address
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name)) {
    return true;
  }
  // Name matches the local part of the email (before @)
  if (email) {
    const localPart = email.split("@")[0];
    if (name.toLowerCase() === localPart?.toLowerCase()) {
      return true;
    }
  }
  return false;
}

/**
 * Get the display name for a task's contact.
 * Prefers joined contact first_name + last_name, falls back to snapshot name.
 * Returns null if name is derived from email so caller can show email instead.
 */
function getContactDisplayName(task: SupportTask): string | null {
  const snapshot = task.customer_snapshot as CustomerSnapshot;
  const email = task.contact?.email || snapshot?.email;

  const contactName =
    task.contact?.first_name || task.contact?.last_name
      ? `${task.contact.first_name || ""} ${task.contact.last_name || ""}`.trim()
      : null;

  if (contactName && !isNameFromEmail(contactName, email)) {
    return contactName;
  }

  const snapshotName = snapshot?.name;
  if (snapshotName && !isNameFromEmail(snapshotName, email)) {
    return snapshotName;
  }

  return null;
}

/**
 * Format task_number as a ticket ID (e.g. ST-00042)
 */
function formatTaskNumber(taskNumber: number): string {
  return `ST-${String(taskNumber).padStart(5, "0")}`;
}

// Templates that should auto-check "Mark task as complete" checkbox
// These indicate a resolution/completion vs interim activity like leaving a voicemail
const COMPLETING_TEMPLATES = [
  "Spoke - Payment Arrangement",
  "Spoke - Cancelled",
  "Issue Resolved",
  "Payment Received",
  "Unable to Reach - Disconnected", // Final resolution - can't contact
];

// Predefined tags for quick selection on resolution
const RESOLUTION_TAGS = [
  "Contacted",
  "Left Voicemail",
  "No Answer",
  "Callback Scheduled",
  "Payment Arranged",
  "Account Cancelled",
  "Issue Resolved",
  "Escalated",
];

// Tab configuration. Two virtual tabs that don't map 1:1 to a task status:
//   - "past_due": pending/in_progress past due_date
//   - "unassigned": pending/in_progress with no agent (leadership triage
//     queue; 2026-05-20 request — replaces the previous Cancelled tab on
//     the overview). Cancelled is still a valid DB status but no longer
//     a surfaced tab.
const TABS: { key: TaskTabKey; label: string }[] = [
  { key: "past_due", label: "Past Due" },
  { key: "pending", label: "Pending" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
  { key: "unassigned", label: "Unassigned" },
];

const SupportTasks: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  // Get returnToTaskId from location state (passed when coming back from contact detail)
  const returnToTaskId = (location.state as { returnToTaskId?: string })
    ?.returnToTaskId;

  // Store only the agent ID - derive the full agent from the agents array
  // This ensures badge counts update when the agents query is refetched.
  // The "__unassigned__" sentinel (imported from supportTasksAgentView) is a
  // distinct filter mode the dropdown can toggle into; treated separately from
  // agent selection.
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<SupportTask | null>(null);
  const [activeTab, setActiveTab] = useState<TaskTabKey>("pending");
  const [filter, setFilter] = useState<TaskFilter>({
    taskType: "all",
    searchQuery: "",
  });
  const debouncedSearchQuery = useDebounce(filter.searchQuery, 500);

  // CSV upload modal removed — tasks come via API only
  // Agent management panel state (Support Admin only)
  const [showAgentManagement, setShowAgentManagement] = useState(false);
  // Which agent the panel should open on. Set when a name is clicked in the Agent
  // Activity table; null when opened via the "Manage Agents" button, which still
  // wants the full list.
  const [agentPanelInitialId, setAgentPanelInitialId] = useState<UUID | null>(
    null,
  );
  // Create task modal state (Support Admin and Support Agent)
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  // Follow-up task modal state
  const [followUpParentTask, setFollowUpParentTask] =
    useState<SupportTask | null>(null);
  // Escalation modal state
  const [escalationModalOpen, setEscalationModalOpen] = useState(false);
  const [escalationReason, setEscalationReason] = useState("");
  const [taskToEscalate, setTaskToEscalate] = useState<SupportTask | null>(
    null,
  );

  // Role-based access
  const isSupportAgent = user?.role === UserRole.SUPPORT_AGENT;
  const isSupportAdmin =
    user?.role === UserRole.SUPPORT_ADMIN ||
    user?.role === UserRole.SITE_ADMIN ||
    user?.role === UserRole.SUPER_ADMIN ||
    user?.role === UserRole.ADMIN;

  // Bulk selection state (for admins)
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(
    new Set(),
  );
  const [_showBulkReassign, setShowBulkReassign] = useState(false);
  const [bulkReassignAgentId, setBulkReassignAgentId] = useState<string>("");

  // Tag filtering state
  const [selectedTagFilter, setSelectedTagFilter] = useState<string>("");

  // Due Today filter state
  const [showDueTodayOnly, setShowDueTodayOnly] = useState(false);

  // Resolution modal state
  const [resolutionModalOpen, setResolutionModalOpen] = useState(false);
  const [taskToResolve, setTaskToResolve] = useState<SupportTask | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [resolutionTags, setResolutionTags] = useState<string[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  // Status selector in modal (empty string = no change)
  const [newStatus, setNewStatus] = useState<TaskStatus | "">("");
  // Due date edit in modal
  const [newDueDate, setNewDueDate] = useState<string>("");
  // Task type edit in detail panel
  const [isEditingTaskType, setIsEditingTaskType] = useState(false);

  // Interaction logging state
  const [logInteraction, setLogInteraction] = useState(false);
  const [interactionMethod, setInteractionMethod] =
    useState<InteractionMethod>("phone_call");
  const [interactionOutcome, setInteractionOutcome] = useState<
    InteractionOutcome | ""
  >("");
  const [followUpDate, setFollowUpDate] = useState<string>("");

  // Customer tag input state
  const [newCustomerTag, setNewCustomerTag] = useState("");

  // Quick Member Lookup state
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const memberSearchRef = useRef<HTMLDivElement>(null);

  // Fetch current user's agent profile (for Support Agent role)
  // Loaded for EVERY role (2026-10-03): anyone with an active support_agents row can be the
  // supervisor a task is escalated to, and needs the "Escalated to me" view.
  const { data: currentAgentProfile, isFetched: agentProfileFetched } =
    useQuery({
      queryKey: ["support-agent-profile", user?.id],
      queryFn: () => getAgentByUserId(user!.id),
      enabled: !!user?.id,
    });
  const canSeeEscalated = canViewEscalatedToMe(currentAgentProfile);

  // Auto-select agent for Support Agent role
  useEffect(() => {
    if (isSupportAgent && currentAgentProfile && !selectedAgentId) {
      setSelectedAgentId(currentAgentProfile.id);
    }
  }, [isSupportAgent, currentAgentProfile, selectedAgentId]);

  // Fetch agents (for Admin role - agent list)
  const { data: agents = [], isLoading: agentsLoading } = useQuery({
    queryKey: ["support-agents"],
    queryFn: getAgents,
    // Support Agents don't need the full list
    enabled: !isSupportAgent,
  });

  // Filter-aware count snapshot for the current TaskFilter
  // (taskType + searchQuery + tag). Drives sidebar header totals,
  // per-agent badges (open buckets), and tab counts. Open buckets
  // respect the filter; completed/cancelled are lifetime per the
  // get_support_task_counts RPC contract.
  const { data: taskCounts } = useQuery({
    queryKey: [
      "support-tasks",
      "counts",
      filter.taskType,
      debouncedSearchQuery,
      selectedTagFilter,
    ],
    queryFn: () =>
      getSupportTaskCounts({
        taskType:
          filter.taskType && filter.taskType !== "all" ? filter.taskType : null,
        search: debouncedSearchQuery || null,
        tags: selectedTagFilter ? [selectedTagFilter] : null,
      }),
    enabled: !isSupportAgent,
  });

  const byAgentCountMap = useMemo(() => {
    const m = new Map<string, SupportTaskCountsByAgent>();
    if (taskCounts?.by_agent) {
      for (const row of taskCounts.by_agent) {
        m.set(row.agent_id, row);
      }
    }
    return m;
  }, [taskCounts]);

  // Legacy unfiltered unassigned count — retained as a fallback for
  // the brief window before `taskCounts` lands. Once `taskCounts` is
  // present, `taskCounts.unassigned_open` supersedes it (filter-aware).
  const { data: unassignedTaskCount = 0 } = useQuery({
    queryKey: ["support-tasks", "unassigned-count"],
    queryFn: getUnassignedTaskCount,
    enabled: isSupportAdmin,
  });

  // Derive the current agent view (selectedAgent / unassignedOnly /
  // tasksQueryEnabled) from the selection + role. Logic lives in
  // resolveAgentView (unit-tested in supportTasksAgentView.test.ts).
  //
  // Support agents default to their own profile but can switch to the
  // "Unassigned" sentinel to triage tasks that failed to route (agent_id
  // null); the sentinel is agent-independent so selectedAgent is null and the
  // unassigned path runs. They are never given the cross-agent list, so a
  // support agent can only ever scope to "my tasks" or "unassigned".
  const { selectedAgent, unassignedOnly, escalatedTo, tasksQueryEnabled } =
    useMemo(
      () =>
        resolveAgentView({
          selectedAgentId,
          isSupportAgent,
          isSupportAdmin,
          currentAgentProfile,
          agents,
        }),
      [
        agents,
        selectedAgentId,
        isSupportAgent,
        isSupportAdmin,
        currentAgentProfile,
      ],
    );

  // Helper to set agent (stores ID, not the full object)
  const setSelectedAgent = (agent: SupportAgent | null) => {
    setSelectedAgentId(agent?.id || null);
    setSelectedTask(null);
  };

  // Fetch available tags for filtering
  const { data: availableTags = [] } = useQuery({
    queryKey: ["support-tags"],
    queryFn: getUniqueTags,
  });

  // Fetch resolution templates
  const { data: resolutionTemplates = [] } = useQuery({
    queryKey: ["resolution-templates", taskToResolve?.task_type],
    queryFn: () => getResolutionTemplates(taskToResolve?.task_type),
    enabled: resolutionModalOpen,
  });

  // Quick Member Lookup search
  const {
    data: memberSearchResults = [],
    isFetching: memberSearching,
    isError: memberSearchFailed,
  } = useQuery({
    queryKey: ["member-search", memberSearchQuery],
    queryFn: () => searchContacts(memberSearchQuery, 10),
    enabled: contactPickerCanSearch(memberSearchQuery),
    // One retry only: a failing search should say so, not spin for ~7 s.
    retry: 1,
  });

  // Close member dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        memberSearchRef.current &&
        !memberSearchRef.current.contains(event.target as Node)
      ) {
        setShowMemberDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch linked interactions for selected task
  const { data: linkedInteractions = [] } = useQuery({
    queryKey: ["linked-interactions", selectedTask?.id],
    queryFn: () => getLinkedInteractions(selectedTask!.id),
    enabled: !!selectedTask,
  });

  // Fetch parent task (if this is a follow-up task)
  const { data: parentTask } = useQuery({
    queryKey: ["parent-task", selectedTask?.parent_task_id],
    queryFn: () => getTaskById(selectedTask!.parent_task_id!),
    enabled: !!selectedTask?.parent_task_id,
  });

  // Fetch child (follow-up) tasks for selected task
  const { data: childTasks = [] } = useQuery({
    queryKey: ["child-tasks", selectedTask?.id],
    queryFn: () => getChildTasks(selectedTask!.id),
    enabled: !!selectedTask,
  });

  // Resolve customer_snapshot.member_id to a contact row so the support-task
  // detail view can render the Member ID as a link. Matches on contacts.
  // membership_id which is populated by the Zuora webhook from MemberNumber__c
  // (primary) or SecondaryMemberNumber__c (secondary), so the link routes
  // to whichever record owns that membership id.
  const memberIdFromSnapshot = (
    selectedTask?.customer_snapshot as CustomerSnapshot | undefined
  )?.member_id;
  const { data: contactByMembershipId } = useQuery({
    queryKey: ["contact-by-membership-id", memberIdFromSnapshot],
    queryFn: () => getContactByMembershipId(memberIdFromSnapshot!),
    enabled: !!memberIdFromSnapshot,
    staleTime: 60_000,
  });

  // Activity timeline expanded state
  const [activityTimelineExpanded, setActivityTimelineExpanded] =
    useState(false);

  // Reset timeline expansion and task type edit when task changes
  useEffect(() => {
    setActivityTimelineExpanded(false);
    setIsEditingTaskType(false);
  }, [selectedTask?.id]);

  // Fetch tasks for selected agent (with tag and due today filtering).
  // The query key includes `unassignedOnly` separately so "All Agents" and
  // "Unassigned" views don't share a cache bucket.
  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: [
      "support-tasks",
      escalatedTo
        ? `escalated:${escalatedTo}`
        : (selectedAgent?.id ?? (unassignedOnly ? "unassigned" : "all")),
      activeTab,
      filter.taskType,
      debouncedSearchQuery,
      selectedTagFilter,
      showDueTodayOnly,
    ],
    queryFn: () => {
      const debouncedFilter = { ...filter, searchQuery: debouncedSearchQuery };

      // ⭐ SEARCHING SPANS EVERY STATUS (reported 2026-09-23).
      // The search matcher runs CLIENT-SIDE over whatever the active tab already loaded, so a
      // completed task was unfindable from the Pending tab however you spelled it -- you had to
      // guess the status first. When a search term is present we therefore ignore the tab and
      // query all statuses (status: null), keeping the agent, unassigned and task-type filters.
      // With no search term the tabs behave exactly as before.
      if ((debouncedSearchQuery ?? "").trim()) {
        return getAgentTasks(
          selectedAgent?.id ?? undefined,
          null,
          debouncedFilter,
          "due_date",
          "asc",
          unassignedOnly,
          escalatedTo,
        );
      }

      // Handle "past_due" virtual tab - uses a different query function
      if (activeTab === "past_due") {
        return getPastDueTasks(
          selectedAgent?.id ?? undefined,
          debouncedFilter,
          "due_date",
          "asc",
          unassignedOnly,
          escalatedTo,
        );
      }
      // Handle "unassigned" virtual tab — leadership triage queue.
      // Always queries agent_id IS NULL across pending+in_progress, so
      // the agent sidebar selection and the unassigned-dropdown flag
      // are intentionally ignored for this tab.
      if (activeTab === "unassigned") {
        return getUnassignedTasks(debouncedFilter, "due_date", "asc");
      }
      // Regular status tabs
      return getAgentTasks(
        selectedAgent?.id ?? undefined,
        activeTab as TaskStatus,
        debouncedFilter,
        "due_date",
        "asc",
        unassignedOnly,
        escalatedTo,
      );
    },
    // For admins: always enabled (All Agents view when no agent selected).
    // For agents: enabled when their profile is loaded (my tasks) OR when they
    // switch to the Unassigned triage view. See resolveAgentView.
    enabled: tasksQueryEnabled,
    select: (data) => {
      let filteredData = data;

      // Filter by tag if selected
      if (selectedTagFilter) {
        filteredData = filteredData.filter((task) => {
          const snapshot = task.customer_snapshot as CustomerSnapshot;
          return (
            snapshot.tags?.includes(selectedTagFilter) ||
            task.tags?.includes(selectedTagFilter)
          );
        });
      }

      // Filter to due today only
      if (showDueTodayOnly) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split("T")[0];
        filteredData = filteredData.filter((task) => {
          if (!task.due_date) {
            return false;
          }
          const taskDueDate = task.due_date.split("T")[0];
          return taskDueDate === todayStr;
        });
      }

      return filteredData;
    },
  });

  // Every tab's count, loaded up front for scoped views (2026-10-03). Support agents had real
  // numbers only on the SELECTED tab; every other tab read 0 (the admin counts RPC is not used
  // for them). Same scope + filters as the lists. Key starts with "support-tasks", so every
  // existing invalidateQueries(["support-tasks"]) after an edit refreshes it too.
  const useScopedTabCounts = isSupportAgent || !!escalatedTo;
  const { data: scopedTabCounts } = useQuery({
    queryKey: [
      "support-tasks",
      "tab-counts",
      escalatedTo
        ? `escalated:${escalatedTo}`
        : (selectedAgent?.id ?? (unassignedOnly ? "unassigned" : "all")),
      filter.taskType,
      debouncedSearchQuery,
      selectedTagFilter,
      showDueTodayOnly,
    ],
    queryFn: () =>
      getSupportTaskTabCounts(
        {
          agentId: selectedAgent?.id ?? null,
          unassignedOnly,
          escalatedTo,
        },
        {
          taskType: filter.taskType,
          searchQuery: debouncedSearchQuery,
          tag: selectedTagFilter || null,
          dueTodayOnly: showDueTodayOnly,
        },
      ),
    enabled: useScopedTabCounts && tasksQueryEnabled,
  });

  // Open tasks escalated to me, for the "Escalated to me (N)" option. Filter-independent.
  const { data: escalatedToMeCounts } = useQuery({
    queryKey: [
      "support-tasks",
      "tab-counts",
      "escalated-badge",
      currentAgentProfile?.id,
    ],
    queryFn: () =>
      getSupportTaskTabCounts(
        { escalatedTo: currentAgentProfile!.id },
        { taskType: "all" },
      ),
    enabled: canSeeEscalated,
  });
  const escalatedToMeOpen =
    (escalatedToMeCounts?.pending ?? 0) +
    (escalatedToMeCounts?.in_progress ?? 0);

  // Fetch activity counts for all visible tasks
  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);
  const { data: activityCounts = new Map() } = useQuery({
    queryKey: ["activity-counts", taskIds],
    queryFn: () => getActivityCountsForTasks(taskIds),
    enabled: taskIds.length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Reassign single task mutation
  const reassignMutation = useMutation({
    mutationFn: ({
      taskId,
      newAgentId,
    }: {
      taskId: string;
      newAgentId: string;
    }) => reassignTask(taskId, newAgentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["support-agents"] });
    },
  });

  // Bulk reassign tasks mutation
  const bulkReassignMutation = useMutation({
    mutationFn: ({
      taskIds,
      newAgentId,
    }: {
      taskIds: string[];
      newAgentId: string;
    }) => bulkReassignTasks(taskIds, newAgentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["support-agents"] });
      setSelectedTaskIds(new Set());
      setShowBulkReassign(false);
      setBulkReassignAgentId("");
    },
  });

  // Bulk SOFT-delete mutation. Soft by design: the row survives, disappears from every
  // authenticated view via a RESTRICTIVE RLS policy, and stays visible to the API path so a
  // deleted task keeps suppressing a re-send of the same type for that member.
  const bulkDeleteMutation = useMutation({
    mutationFn: ({ taskIds, reason }: { taskIds: string[]; reason?: string }) =>
      bulkSoftDeleteTasks(taskIds, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["support-agents"] });
      setSelectedTaskIds(new Set());
    },
  });

  // A refused write (SupportTaskNotUpdatedError) says WHY; anything else keeps a generic line.
  const taskWriteErrorMessage = (err: unknown, fallback: string) => {
    if (err instanceof SupportTaskNotUpdatedError) {
      return err.message;
    }
    // Migration 200's supervisor guard explains itself; show its sentence, not a fallback.
    const msg = (err as { message?: string } | null)?.message ?? "";
    if (msg.startsWith("SUPERVISOR_EDIT_OUT_OF_SCOPE:")) {
      return msg.replace("SUPERVISOR_EDIT_OUT_OF_SCOPE:", "").trim();
    }
    return fallback;
  };

  // The viewer acts on this task ONLY as the supervisor it is escalated to (not admin, not the
  // assigned agent). Migration 200 lets them return it, complete it, or add notes -- nothing else
  // -- so the resolution modal offers only those.
  const isEscalationSupervisorOnly = (task: SupportTask | null) =>
    !!task &&
    !isSupportAdmin &&
    canSeeEscalated &&
    !!task.escalated_to &&
    task.escalated_to === currentAgentProfile?.id &&
    task.agent_id !== currentAgentProfile?.id;

  // Mutation for updating task status
  const statusMutation = useMutation({
    mutationFn: ({
      taskId,
      status,
      resolutionData,
    }: {
      taskId: string;
      status: TaskStatus;
      resolutionData?: TaskResolutionData;
    }) => updateTaskStatus(taskId, status, resolutionData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["support-agents"] });
      queryClient.invalidateQueries({ queryKey: ["interactions"] });
      setSelectedTask(null);
      // Reset modal state
      setResolutionModalOpen(false);
      setTaskToResolve(null);
      setResolutionNotes("");
      setResolutionTags([]);
      setSelectedTemplateId(null);
      setLogInteraction(false);
      setInteractionMethod("phone_call");
      setInteractionOutcome("");
    },
    // Previously no onError: a refused write left the modal open with no message.
    onError: (err) => {
      showToast(
        "error",
        taskWriteErrorMessage(err, "Failed to update the task status"),
      );
    },
  });

  // Mutation for updating task type
  const updateTaskTypeMutation = useMutation({
    mutationFn: ({
      taskId,
      taskType,
    }: {
      taskId: string;
      taskType: SupportTaskType;
    }) => updateTaskType(taskId, taskType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      setIsEditingTaskType(false);
      showToast("success", "Task type updated");
    },
    onError: (err) => {
      showToast(
        "error",
        taskWriteErrorMessage(err, "Failed to update task type"),
      );
    },
  });

  // Mutation for customer tags
  const addTagMutation = useMutation({
    mutationFn: ({ taskId, tag }: { taskId: string; tag: string }) =>
      addCustomerTag(taskId, tag),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      if (updatedTask) {
        setSelectedTask(updatedTask);
      }
    },
    onError: (err) => {
      showToast("error", taskWriteErrorMessage(err, "Failed to update tags"));
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: ({ taskId, tag }: { taskId: string; tag: string }) =>
      removeCustomerTag(taskId, tag),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      if (updatedTask) {
        setSelectedTask(updatedTask);
      }
    },
    onError: (err) => {
      showToast("error", taskWriteErrorMessage(err, "Failed to update tags"));
    },
  });

  // Escalation mutation
  const escalateMutation = useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason: string }) =>
      escalateTask(taskId, reason),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["support-agents"] });
      // null = saved, but the re-read is no longer visible to this user.
      setSelectedTask(updatedTask);
      setEscalationModalOpen(false);
      setTaskToEscalate(null);
      setEscalationReason("");
      showToast("success", "Task escalated");
    },
    // The modal also shows the message inline; the toast covers a closed modal.
    onError: (err) => {
      showToast("error", taskWriteErrorMessage(err, "Failed to escalate task"));
    },
  });

  // De-escalation mutation
  const deescalateMutation = useMutation({
    mutationFn: (taskId: string) => deescalateTask(taskId),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["support-agents"] });
      setSelectedTask(updatedTask);
      showToast("success", "Task returned to the original agent");
    },
    // Previously no onError: a refused de-escalation failed silently.
    onError: (err) => {
      showToast(
        "error",
        taskWriteErrorMessage(err, "Failed to de-escalate task"),
      );
    },
  });

  // Clear the open task only when the USER changes tab or scope. This used to be an effect on
  // [selectedAgentId, activeTab], which also fired after PROGRAMMATIC changes -- the deep link
  // sets the tab and scope and then selects the task, so the effect closed the panel it had just
  // opened (2026-10-03), and the support-agent auto-select did the same once the profile loaded.
  const changeTab = (tab: TaskTabKey) => {
    setActiveTab(tab);
    setSelectedTask(null);
  };
  const changeAgentSelection = (id: string | null) => {
    setSelectedAgentId(id);
    setSelectedTask(null);
  };

  // Handle ?task=<id> deep link from share URL. Waits for the viewer's agent profile so an
  // escalated task can open in "Escalated to me" (the view that actually lists it).
  const deepLinkTaskIdRef = useRef<string | null>(searchParams.get("task"));
  useEffect(() => {
    const taskParam = deepLinkTaskIdRef.current;
    if (!taskParam || (!!user?.id && !agentProfileFetched)) {
      return;
    }
    deepLinkTaskIdRef.current = null;
    setSearchParams({}, { replace: true });
    getTaskById(taskParam).then((task) => {
      if (!task) {
        showToast(
          "error",
          "That task was not found, or you don't have access to it.",
        );
        return;
      }
      const view = resolveDeepLinkView(task, {
        isSupportAdmin,
        currentAgentProfile,
      });
      setActiveTab(view.tab);
      if (view.agentSelection !== undefined) {
        setSelectedAgentId(view.agentSelection);
      }
      setSelectedTask(task);
    });
  }, [agentProfileFetched, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle return from contact detail - navigate to the task
  useEffect(() => {
    if (returnToTaskId) {
      // Fetch the task to get its current status and agent
      getTaskById(returnToTaskId).then((task) => {
        if (task) {
          // Set the appropriate tab based on task status
          setActiveTab(
            resolveDeepLinkView(task, { isSupportAdmin, currentAgentProfile })
              .tab,
          );
          // For admins, set agent or stay on "All Agents"
          if (isSupportAdmin && task.agent_id) {
            // If admin, we can stay on "All Agents" view or select the specific agent
            // Staying on current view (All Agents or specific agent) is fine
          }
          // Select the task
          setSelectedTask(task);
        }
      });
      // Clear the location state so it doesn't trigger again
      window.history.replaceState({}, document.title);
    }
  }, [returnToTaskId, isSupportAdmin]);

  // Handle task selection - mark as viewed (sets opened_at) without changing status
  const handleTaskSelect = (task: SupportTask) => {
    setSelectedTask(task);
    if (task.status === "pending" && !task.opened_at) {
      markTaskViewed(task.id);
    }
  };

  // Open resolution modal — unified modal for logging activity + changing status
  const openResolutionModal = (task: SupportTask) => {
    setTaskToResolve(task);
    setResolutionNotes("");
    setResolutionTags([]);
    setSelectedTemplateId(null);
    setNewStatus(""); // Empty = no status change
    setNewDueDate(task.due_date?.split("T")[0] || "");
    setLogInteraction(false);
    setInteractionOutcome("");
    setFollowUpDate("");
    setResolutionModalOpen(true);
  };

  // Submit resolution (or just log activity if no status change)
  const handleSubmitResolution = async () => {
    if (!taskToResolve || !user) {
      return;
    }

    // Require notes when marking as completed
    if (newStatus === "completed" && !resolutionNotes.trim()) {
      return; // Button is disabled, but guard here too
    }

    const resolutionData: TaskResolutionData = {};
    if (resolutionNotes.trim()) {
      resolutionData.notes = resolutionNotes.trim();
    }
    if (resolutionTags.length > 0) {
      resolutionData.tags = resolutionTags;
    }

    // Create interaction if checkbox is checked.
    // Filed under the CUSTOMER: their contact and their organization when they have one, otherwise no
    // organization (migration 2026-10-06-321 allows that for rows tied to a support task). It used to
    // fall back to the operator's own organization (gap list M7) and, since profiles has no
    // organization_id, silently skipped the note for every customer without an organization.
    if (logInteraction && taskToResolve.contact_id) {
      try {
        const snapshot = taskToResolve.customer_snapshot as CustomerSnapshot;
        const customerOrganizationId =
          (taskToResolve.contact as { organization_id?: string | null })
            ?.organization_id || null;
        const interaction = await createInteraction(
          buildSupportTaskInteraction({
            taskId: taskToResolve.id,
            contactId: taskToResolve.contact_id,
            customerOrganizationId,
            userId: user.id,
            title: `${TASK_TYPE_LABELS[taskToResolve.task_type] || taskToResolve.task_type} - ${getContactDisplayName(taskToResolve) || snapshot.email || snapshot.name}`,
            method: interactionMethod,
            description:
              resolutionNotes.trim() ||
              (newStatus
                ? `Task ${newStatus} for ${getContactDisplayName(taskToResolve) || snapshot.email || snapshot.name}`
                : `Activity logged for ${getContactDisplayName(taskToResolve) || snapshot.email || snapshot.name}`),
            outcome: (interactionOutcome as InteractionOutcome) || undefined,
            nextFollowUpDate: followUpDate || undefined,
            tags: resolutionTags.length > 0 ? resolutionTags : undefined,
          }) as unknown as Parameters<typeof createInteraction>[0],
        );

        if (interaction?.id) {
          await linkInteractionToTask(taskToResolve.id, interaction.id);
        }
      } catch (error) {
        console.error("Failed to create interaction:", error);
        // Was console-only: the operator believed the activity was logged.
        showToast(
          "warning",
          `The activity could not be logged: ${
            error && typeof error === "object" && "message" in error
              ? String((error as { message: unknown }).message)
              : "unknown error"
          }`,
        );
      }
    }

    // Update due date if changed (independent of status)
    const originalDueDate = taskToResolve.due_date?.split("T")[0] || "";
    if (newDueDate && newDueDate !== originalDueDate) {
      try {
        await updateTaskDueDate(taskToResolve.id, newDueDate);
      } catch (error) {
        console.error("Failed to update due date:", error);
        // Used to be console-only; a refused due-date write looked saved.
        showToast(
          "error",
          taskWriteErrorMessage(error, "Failed to update the due date"),
        );
        if (error instanceof SupportTaskNotUpdatedError) {
          return; // the status change would be refused for the same reason
        }
      }
    }

    // If no status change, just log activity and close
    if (!newStatus || newStatus === taskToResolve.status) {
      // "Add note" (2026-10-03): notes typed without a status change used to be DROPPED unless
      // "Log interaction" was ticked. Append them to the task's notes instead.
      if (resolutionNotes.trim() && !logInteraction) {
        try {
          await appendTaskNote(
            taskToResolve.id,
            resolutionNotes.trim(),
            user.full_name || user.email || "",
          );
          showToast("success", "Note added");
        } catch (error) {
          console.error("Failed to add note:", error);
          showToast(
            "error",
            taskWriteErrorMessage(error, "Failed to add the note"),
          );
          return;
        }
      }
      queryClient.invalidateQueries({
        queryKey: ["linked-interactions", taskToResolve.id],
      });
      queryClient.invalidateQueries({ queryKey: ["support-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["support-agents"] });
      setResolutionModalOpen(false);
      setSelectedTask(null);
      return;
    }

    // Change status
    statusMutation.mutate({
      taskId: taskToResolve.id,
      status: newStatus,
      resolutionData:
        Object.keys(resolutionData).length > 0 ? resolutionData : undefined,
    });
  };

  // Reopen task (move back to in_progress)
  const handleReopenTask = (task: SupportTask) => {
    statusMutation.mutate({
      taskId: task.id,
      status: "in_progress",
    });
  };

  const toggleResolutionTag = (tag: string) => {
    setResolutionTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleAddCustomerTag = () => {
    if (selectedTask && newCustomerTag.trim()) {
      addTagMutation.mutate({
        taskId: selectedTask.id,
        tag: newCustomerTag.trim(),
      });
      setNewCustomerTag("");
    }
  };

  const handleRemoveCustomerTag = (tag: string) => {
    if (selectedTask) {
      removeTagMutation.mutate({ taskId: selectedTask.id, tag });
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDaysOverdue = (dueDate: string | null) => {
    if (!dueDate) {
      return 0;
    }
    // 2026-06-02: support_tasks.due_date is a DATE column (no time).
    // PostgREST returns it as "YYYY-MM-DD". `new Date("YYYY-MM-DD")`
    // parses as UTC midnight, which in any negative-offset timezone
    // (US) lands on the PRIOR calendar day. That made due_date=today
    // appear as "1 day overdue" and pushed reports of "tomorrow shows
    // overdue" depending on creation timing. Parse the YYYY-MM-DD
    // parts manually so the Date object lands at LOCAL midnight on
    // the intended day.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let due: Date;
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dueDate);
    if (dateOnlyMatch) {
      const [, yStr, mStr, dStr] = dateOnlyMatch;
      due = new Date(Number(yStr), Number(mStr) - 1, Number(dStr));
    } else {
      // Defensive fallback for any caller passing a full timestamp.
      due = new Date(dueDate);
      due.setHours(0, 0, 0, 0);
    }
    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Bulk selection handlers (Admin only)
  const toggleTaskSelection = (taskId: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const selectAllTasks = () => {
    setSelectedTaskIds(new Set(tasks.map((t) => t.id)));
  };

  const clearSelection = () => {
    setSelectedTaskIds(new Set());
  };

  const handleBulkReassign = () => {
    if (selectedTaskIds.size > 0 && bulkReassignAgentId) {
      bulkReassignMutation.mutate({
        taskIds: Array.from(selectedTaskIds),
        newAgentId: bulkReassignAgentId,
      });
    }
  };

  // Deleting a queue is not undoable from the UI, so it asks first and names the count. The RPC is
  // the real gate; this is the operator-facing confirmation.
  const handleBulkDelete = () => {
    const count = selectedTaskIds.size;
    if (count === 0) {
      return;
    }
    const ok = window.confirm(
      `Delete ${count} task${count > 1 ? "s" : ""}?\n\n` +
        `They will be removed from every queue and report. Because the sending system re-emits ` +
        `recommendations, a deleted task also stops the same task type being re-created for that ` +
        `member inside the de-duplication window.`,
    );
    if (!ok) {
      return;
    }
    bulkDeleteMutation.mutate({
      taskIds: Array.from(selectedTaskIds),
      reason: `Bulk delete by ${user?.email ?? "an admin"}`,
    });
  };

  const handleSingleReassign = (taskId: string, newAgentId: string) => {
    reassignMutation.mutate({ taskId, newAgentId });
  };

  const getTabCount = (tabKey: TaskTabKey): number => {
    if (useScopedTabCounts) {
      return scopedTabCounts?.[tabKey] ?? 0;
    }
    // Unassigned tab uses filter-aware `unassigned_open` from the
    // RPC when available; falls back to the legacy unfiltered count
    // during the brief pre-load window.
    if (tabKey === "unassigned") {
      return taskCounts?.unassigned_open ?? unassignedTaskCount;
    }
    // Per-agent view: filter-aware buckets come from byAgentCountMap;
    // completed is lifetime per the RPC contract (the "X done" label
    // is a lifetime counter and intentionally not filter-scoped).
    if (selectedAgent) {
      const agentRow = byAgentCountMap.get(selectedAgent.id);
      switch (tabKey) {
        case "past_due":
          return agentRow?.past_due_count ?? 0;
        case "pending":
          return agentRow?.pending_count ?? 0;
        case "in_progress":
          return agentRow?.in_progress_count ?? 0;
        case "completed":
          return selectedAgent.completed_count;
        default:
          return 0;
      }
    }
    // "All Agents" view: filter-aware totals from RPC; completed is
    // a lifetime sum across all agents.
    if (isSupportAdmin) {
      switch (tabKey) {
        case "past_due":
          return taskCounts?.past_due_open ?? 0;
        case "pending":
          return taskCounts?.total_pending ?? 0;
        case "in_progress":
          return taskCounts?.total_in_progress ?? 0;
        case "completed":
          return agents.reduce((sum, a) => sum + a.completed_count, 0);
        default:
          return 0;
      }
    }
    return 0;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Support Tasks</h1>
            <p className="text-gray-600">
              {isSupportAgent
                ? "Manage your assigned support tasks"
                : "Manage daily support tasks for your team"}
            </p>
            {/* Support agents have no other route to their own book of business:
                the left Agents panel and the Agent Activity table are both
                admin-only, and Agent Management is where an admin would otherwise
                click through. Reuses the same deep link the admin path uses, so
                the contacts page shows the usual banner and a Back to Support
                Tasks link. Rendered only once the agent profile resolves --
                without an id the link would filter to nobody. */}
            {isSupportAgent && currentAgentProfile?.id && (
              <Link
                to={`/contacts?agent=${currentAgentProfile.id}&from=agent`}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                View my assigned contacts
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            )}
          </div>

          {/* Quick Member Lookup */}
          <div className="relative" ref={memberSearchRef}>
            <div className="flex items-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Quick member lookup..."
                  value={memberSearchQuery}
                  onChange={(e) => {
                    setMemberSearchQuery(e.target.value);
                    setShowMemberDropdown(e.target.value.trim().length > 0);
                  }}
                  onFocus={() => {
                    if (memberSearchQuery.trim().length > 0) {
                      setShowMemberDropdown(true);
                    }
                  }}
                  className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Member Search Dropdown */}
            {showMemberDropdown && memberSearchQuery.trim().length > 0 && (
              <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                {memberSearchResults.length === 0 ? (
                  <div
                    data-testid="member-lookup-status"
                    className="p-4 text-sm text-gray-500 text-center"
                  >
                    {contactPickerStatus(
                      memberSearchQuery,
                      memberSearching,
                      memberSearchFailed,
                    )}
                  </div>
                ) : (
                  <div className="py-1">
                    {memberSearchResults.map((contact) => (
                      <Link
                        key={contact.id}
                        to={`/contacts/${contact.id}`}
                        onClick={() => {
                          setShowMemberDropdown(false);
                          setMemberSearchQuery("");
                        }}
                        className="block px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">
                          {contact.first_name} {contact.last_name}
                        </div>
                        <div className="text-sm text-gray-500 flex flex-wrap gap-x-3">
                          {contact.email && <span>{contact.email}</span>}
                          {contact.phone_number && (
                            <span>{contact.phone_number}</span>
                          )}
                        </div>
                        {contact.member_status && (
                          <span
                            className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                              contact.member_status.toLowerCase() === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {contact.member_status}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {/* Show for Support Admins (full view) and Support Agents (filtered to their tasks) */}
      {(isSupportAdmin || (isSupportAgent && currentAgentProfile)) && (
        <div className="flex-shrink-0 overflow-x-auto px-6 pt-4">
          <SupportAdminDashboard
            onOpenImportModal={undefined}
            onOpenAgentManagement={
              isSupportAdmin
                ? () => {
                    // Button route: always the list, so clear any agent left over
                    // from a previous name click.
                    setAgentPanelInitialId(null);
                    setShowAgentManagement(true);
                  }
                : undefined
            }
            onOpenCreateTaskModal={() => setCreateTaskModalOpen(true)}
            agentId={isSupportAgent ? currentAgentProfile?.id : undefined}
            isAgentView={isSupportAgent}
            taskType={filter.taskType}
          />
        </div>
      )}

      {/* Agent Activity summary and its chart REMOVED 2026-09-23 at the support team's request
          ("Please remove Agent Activity and the graph as this is not something we need").
          The component and its tests are retained; re-mount here to restore it. Agents are still
          reachable through the left panel and the Manage Agents button, so nothing is orphaned. */}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Agent List - Left Panel (Hidden for Support Agents) */}
        {!isSupportAgent && (
          <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="p-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Agents
              </h2>
              {agentsLoading ? (
                <div className="text-gray-500 text-sm">Loading agents...</div>
              ) : agents.length === 0 ? (
                <div className="text-gray-500 text-sm">
                  No agents found. Import tasks to create agents.
                </div>
              ) : (
                <div className="space-y-1">
                  {/* All Agents option */}
                  <button
                    onClick={() => setSelectedAgent(null)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedAgent === null
                        ? "bg-primary-100 text-primary-800 border border-primary-200"
                        : "hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="font-medium flex items-center gap-2">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      All Agents
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-2">
                      {(taskCounts?.past_due_open ?? 0) > 0 && (
                        <span className="text-red-600 font-medium">
                          {taskCounts?.past_due_open ?? 0} overdue
                        </span>
                      )}
                      <span className="text-amber-600">
                        {taskCounts?.total_pending ?? 0} new
                      </span>
                      <span className="text-blue-600">
                        {taskCounts?.total_in_progress ?? 0} active
                      </span>
                      <span className="text-green-600">
                        {agents.reduce((sum, a) => sum + a.completed_count, 0)}{" "}
                        done
                      </span>
                    </div>
                  </button>

                  <div className="border-b border-gray-200 my-2" />

                  {/* Individual agents */}
                  {agents.map((agent) => {
                    const row = byAgentCountMap.get(agent.id);
                    const pastDue = row?.past_due_count ?? 0;
                    const pending = row?.pending_count ?? 0;
                    const inProgress = row?.in_progress_count ?? 0;
                    return (
                      <button
                        key={agent.id}
                        onClick={() => setSelectedAgent(agent)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          selectedAgent?.id === agent.id
                            ? "bg-primary-100 text-primary-800 border border-primary-200"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        <div className="font-medium">{agent.name}</div>
                        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-2">
                          {pastDue > 0 && (
                            <span className="text-red-600 font-medium">
                              {pastDue} overdue
                            </span>
                          )}
                          <span className="text-amber-600">{pending} new</span>
                          <span className="text-blue-600">
                            {inProgress} active
                          </span>
                          <span className="text-green-600">
                            {agent.completed_count} done
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Task List - Middle Panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Support agents always get the panel (their own tasks by default,
              plus the Unassigned triage view where selectedAgent is null). */}
          {selectedAgent || isSupportAdmin || isSupportAgent ? (
            <>
              {/* Agent Header & Tabs */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  {escalatedTo ? (
                    "Escalated to me"
                  ) : selectedAgent ? (
                    selectedAgent.name
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      All Agents
                    </>
                  )}
                </h2>

                {/* Tabs */}
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                  {TABS.map((tab) => {
                    const count =
                      tab.key === activeTab && !tasksLoading
                        ? tasks.length
                        : getTabCount(tab.key);
                    // Virtual tabs (past_due, unassigned) aren't keys of
                    // TASK_STATUS_CONFIG; supply their own pill styling.
                    const config =
                      tab.key === "past_due"
                        ? { bgColor: "bg-red-100", color: "text-red-800" }
                        : tab.key === "unassigned"
                          ? { bgColor: "bg-amber-100", color: "text-amber-800" }
                          : TASK_STATUS_CONFIG[tab.key as TaskStatus];
                    return (
                      <button
                        key={tab.key}
                        onClick={() => changeTab(tab.key)}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === tab.key
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {tab.label}
                        <span
                          className={`ml-2 px-2 py-0.5 text-xs rounded-full ${config.bgColor} ${config.color}`}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Filters */}
                <div className="flex items-center gap-4 mt-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    {/* Persistent visible label so the external / agent task ID
                        search affordance is obvious even before any task has an
                        external_task_id set (placeholder text alone vanishes on
                        typing and reads as low-contrast). */}
                    <label
                      htmlFor="support-task-search"
                      className="block text-xs font-medium text-gray-600 mb-1"
                    >
                      Search — name, email, phone, external / agent task ID, or
                      tags
                    </label>
                    <input
                      id="support-task-search"
                      type="text"
                      placeholder="e.g. customer name, email, phone, external / agent task ID, or tag"
                      value={filter.searchQuery}
                      onChange={(e) =>
                        setFilter((f) => ({
                          ...f,
                          searchQuery: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  {/* Agent filter — admin only, sits alongside type filter
                      so operators can pick an agent without navigating the
                      sidebar. Binds directly to selectedAgentId; the empty
                      "all" value renders the cross-agent view. */}
                  {isSupportAdmin && (
                    <select
                      value={selectedAgentId ?? ""}
                      onChange={(e) =>
                        changeAgentSelection(e.target.value || null)
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">All Agents</option>
                      <option value={UNASSIGNED_FILTER_SENTINEL}>
                        Unassigned
                      </option>
                      {canSeeEscalated && (
                        <option value={ESCALATED_FILTER_SENTINEL}>
                          {`Escalated to me (${escalatedToMeOpen})`}
                        </option>
                      )}
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {/* Scoped agent filter — support agents only. Two choices:
                      their own tasks (default) or the Unassigned triage bucket
                      (tasks that failed to route). Deliberately excludes the
                      cross-agent list so a support agent never sees another
                      agent's tasks. */}
                  {isSupportAgent && currentAgentProfile && (
                    <select
                      value={selectedAgentId ?? ""}
                      onChange={(e) =>
                        changeAgentSelection(e.target.value || null)
                      }
                      aria-label="Task scope"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value={currentAgentProfile.id}>My Tasks</option>
                      <option value={UNASSIGNED_FILTER_SENTINEL}>
                        Unassigned
                      </option>
                      {canSeeEscalated && (
                        <option value={ESCALATED_FILTER_SENTINEL}>
                          {`Escalated to me (${escalatedToMeOpen})`}
                        </option>
                      )}
                    </select>
                  )}
                  <select
                    value={filter.taskType || "all"}
                    onChange={(e) =>
                      setFilter((f) => ({
                        ...f,
                        taskType: e.target.value as TaskTypeFilterValue,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Task Types</option>
                    {taskTypeFilterOptions().map((group) => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>

                  {/* Due Today Filter */}
                  <button
                    onClick={() => setShowDueTodayOnly(!showDueTodayOnly)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      showDueTodayOnly
                        ? "bg-amber-100 text-amber-800 border border-amber-300"
                        : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Due Today
                    {showDueTodayOnly && (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>

                  {/* Tag Filter */}
                  {availableTags.length > 0 && (
                    <select
                      value={selectedTagFilter}
                      onChange={(e) => setSelectedTagFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">All Tags</option>
                      {availableTags.map((tag) => (
                        <option key={tag} value={tag}>
                          {tag}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Bulk Actions (Admin only). Hidden on completed/cancelled
                    tabs — reassigning closed tasks is surprising and is
                    explicitly blocked by the bulk_reassign_support_tasks
                    RPC. UI gate is the operator-friendly backstop. */}
                {isSupportAdmin &&
                  tasks.length > 0 &&
                  activeTab !== "completed" && (
                    <div className="mt-3 sticky top-0 z-10 bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap shadow-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={selectAllTasks}
                          className="text-sm text-primary-600 hover:text-primary-800"
                        >
                          Select All ({tasks.length})
                        </button>
                        {selectedTaskIds.size > 0 && (
                          <>
                            <span className="text-gray-400">|</span>
                            <button
                              onClick={clearSelection}
                              className="text-sm text-gray-600 hover:text-gray-800"
                            >
                              Clear ({selectedTaskIds.size} selected)
                            </button>
                          </>
                        )}
                      </div>

                      {selectedTaskIds.size > 0 && (
                        <div className="flex items-center gap-2 ml-auto">
                          <select
                            value={bulkReassignAgentId}
                            onChange={(e) =>
                              setBulkReassignAgentId(e.target.value)
                            }
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">Reassign to...</option>
                            {agents
                              .filter((a) => a.id !== selectedAgent?.id)
                              .map((agent) => (
                                <option key={agent.id} value={agent.id}>
                                  {agent.name}
                                </option>
                              ))}
                          </select>
                          <button
                            onClick={handleBulkReassign}
                            disabled={
                              !bulkReassignAgentId ||
                              bulkReassignMutation.isPending
                            }
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {bulkReassignMutation.isPending
                              ? "Reassigning..."
                              : `Reassign ${selectedTaskIds.size} Task${selectedTaskIds.size > 1 ? "s" : ""}`}
                          </button>
                          <button
                            onClick={handleBulkDelete}
                            disabled={bulkDeleteMutation.isPending}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {bulkDeleteMutation.isPending
                              ? "Deleting..."
                              : `Delete ${selectedTaskIds.size}`}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
              </div>

              {/* Task List */}
              <div className="flex-1 overflow-y-auto p-4">
                {tasksLoading ? (
                  <div className="text-gray-500 text-center py-8">
                    Loading tasks...
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">
                    {/* Virtual tabs (past_due, unassigned) aren't in
                        TASK_STATUS_CONFIG; provide their own labels. */}
                    No{" "}
                    {activeTab === "past_due"
                      ? "past due"
                      : activeTab === "unassigned"
                        ? "unassigned"
                        : TASK_STATUS_CONFIG[
                            activeTab as keyof typeof TASK_STATUS_CONFIG
                          ]?.label.toLowerCase()}{" "}
                    tasks found.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {tasks.map((task) => {
                      const daysOverdue = getDaysOverdue(task.due_date);
                      const isOverdue =
                        daysOverdue > 0 &&
                        task.status !== "completed" &&
                        task.status !== "cancelled";
                      const isSeverlyOverdue = daysOverdue > 7;
                      const statusConfig = TASK_STATUS_CONFIG[task.status];
                      const isSelected = selectedTaskIds.has(task.id);
                      const activityCount = activityCounts.get(task.id) || 0;

                      return (
                        <div
                          key={task.id}
                          className={`p-4 bg-white border rounded-lg transition-all ${
                            selectedTask?.id === task.id
                              ? "border-primary-500 ring-2 ring-primary-200"
                              : isSelected
                                ? "border-blue-400 bg-blue-50"
                                : isSeverlyOverdue
                                  ? "border-red-300 bg-red-50"
                                  : isOverdue
                                    ? "border-amber-200 bg-amber-50"
                                    : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox for bulk selection (Admin only) */}
                            {isSupportAdmin && (
                              <div className="flex-shrink-0 pt-1">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleTaskSelection(task.id);
                                  }}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                              </div>
                            )}

                            {/* Status indicator */}
                            <div
                              className={`mt-1 w-3 h-3 rounded-full flex-shrink-0 ${statusConfig.bgColor}`}
                            />

                            {/* Task Content - Clickable */}
                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => handleTaskSelect(task)}
                            >
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {/* Priority badge (2026-05-23). Only
                                    rendered for non-medium so the default
                                    case stays uncluttered. */}
                                {task.priority &&
                                  task.priority !== "medium" && (
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        TASK_PRIORITY_CONFIG[task.priority]
                                          .bgColor
                                      } ${
                                        TASK_PRIORITY_CONFIG[task.priority]
                                          .color
                                      }`}
                                      title={`Priority: ${TASK_PRIORITY_CONFIG[task.priority].label}`}
                                    >
                                      {
                                        TASK_PRIORITY_CONFIG[task.priority]
                                          .label
                                      }
                                    </span>
                                  )}
                                {/* While SEARCHING, results span every status, so the tab no
                                    longer tells you where a task lives -- say so explicitly.
                                    Hidden during ordinary tab browsing, where it would be noise. */}
                                {(debouncedSearchQuery ?? "").trim() && (
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig.bgColor} ${statusConfig.color}`}
                                    title={`Status: ${statusConfig.label}`}
                                  >
                                    {statusConfig.label}
                                  </span>
                                )}
                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    task.task_type === "birthday_message"
                                      ? "bg-purple-100 text-purple-800"
                                      : task.task_type.startsWith("outbound_")
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-orange-100 text-orange-800"
                                  }`}
                                >
                                  {TASK_TYPE_LABELS[task.task_type] ||
                                    task.task_type}
                                </span>
                                {/* Show agent name (or Unassigned) when not
                                    filtered to a specific agent's view */}
                                {!selectedAgent && (
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                      task.agent
                                        ? "bg-gray-100 text-gray-700"
                                        : "bg-amber-100 text-amber-800"
                                    }`}
                                  >
                                    {task.agent?.name ?? "Unassigned"}
                                  </span>
                                )}
                                {isOverdue && (
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                      isSeverlyOverdue
                                        ? "bg-red-200 text-red-900"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    {daysOverdue} day
                                    {daysOverdue > 1 ? "s" : ""} overdue
                                    {isSeverlyOverdue && " - URGENT"}
                                  </span>
                                )}
                                {(task.customer_snapshot as CustomerSnapshot)
                                  .tags &&
                                  ((task.customer_snapshot as CustomerSnapshot)
                                    .tags?.length ?? 0) > 0 && (
                                    <span className="text-xs text-gray-500">
                                      +
                                      {
                                        (
                                          task.customer_snapshot as CustomerSnapshot
                                        ).tags?.length
                                      }{" "}
                                      tag
                                      {((
                                        task.customer_snapshot as CustomerSnapshot
                                      ).tags?.length ?? 0) > 1
                                        ? "s"
                                        : ""}
                                    </span>
                                  )}
                                {/* Activity count indicator */}
                                {activityCount > 0 && (
                                  <span
                                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700"
                                    title={`${activityCount} logged ${activityCount === 1 ? "activity" : "activities"}`}
                                  >
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                      />
                                    </svg>
                                    {activityCount}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <ContactQuickView
                                  contactId={task.contact_id}
                                  disabled={!task.contact_id}
                                  fromTaskId={task.id}
                                >
                                  <div className="font-medium text-gray-900 hover:text-primary-600 cursor-pointer inline-block">
                                    {getContactDisplayName(task) ||
                                      (
                                        task.customer_snapshot as CustomerSnapshot
                                      )?.email ||
                                      "Unknown"}
                                  </div>
                                </ContactQuickView>
                                {task.task_number && (
                                  <span className="text-xs font-mono text-gray-400">
                                    {formatTaskNumber(task.task_number)}
                                  </span>
                                )}
                                <ExternalTaskId value={task.external_task_id} />
                              </div>
                              <div className="text-sm text-gray-500">
                                Due:{" "}
                                {task.due_date
                                  ? formatDate(task.due_date)
                                  : "—"}
                                {!getContactDisplayName(task)
                                  ? null
                                  : (task.customer_snapshot as CustomerSnapshot)
                                      .email && (
                                      <span className="ml-3">
                                        {
                                          (
                                            task.customer_snapshot as CustomerSnapshot
                                          ).email
                                        }
                                      </span>
                                    )}
                              </div>
                              {task.notes && (
                                <div className="text-xs text-gray-500 mt-1 bg-yellow-50 px-2 py-1 rounded line-clamp-2">
                                  {task.notes}
                                </div>
                              )}
                            </div>

                            {/* Arrow */}
                            <svg
                              className="w-5 h-5 text-gray-400 flex-shrink-0 cursor-pointer"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              onClick={() => handleTaskSelect(task)}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <svg
                  className="w-16 h-16 mx-auto mb-4 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <p className="text-lg font-medium">Select an agent</p>
                <p className="text-sm">
                  Choose an agent from the list to view their tasks
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Task Detail Panel - Right */}
        {selectedTask && (
          <div className="w-96 border-l border-gray-200 bg-white overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Task Details</h3>
                {selectedTask.task_number && (
                  <span className="text-xs font-mono text-gray-400">
                    {formatTaskNumber(selectedTask.task_number)}
                  </span>
                )}
                <ExternalTaskId value={selectedTask.external_task_id} detail />
              </div>
              <button
                onClick={() => setSelectedTask(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    TASK_STATUS_CONFIG[selectedTask.status].bgColor
                  } ${TASK_STATUS_CONFIG[selectedTask.status].color}`}
                >
                  {TASK_STATUS_CONFIG[selectedTask.status].label}
                </span>
                {isSupportAdmin && isEditingTaskType ? (
                  <div className="flex items-center gap-1">
                    <select
                      defaultValue={selectedTask.task_type}
                      autoFocus
                      onChange={(e) => {
                        updateTaskTypeMutation.mutate({
                          taskId: selectedTask.id,
                          taskType: e.target.value as SupportTaskType,
                        });
                        setSelectedTask((t) =>
                          t
                            ? {
                                ...t,
                                task_type: e.target.value as SupportTaskType,
                              }
                            : t,
                        );
                      }}
                      onBlur={() => setIsEditingTaskType(false)}
                      className="text-xs border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      {groupedTaskTypeOptions().map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                ) : (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium cursor-default ${
                      selectedTask.task_type === "birthday_message"
                        ? "bg-purple-100 text-purple-800"
                        : selectedTask.task_type.startsWith("outbound_")
                          ? "bg-blue-100 text-blue-800"
                          : "bg-orange-100 text-orange-800"
                    } ${isSupportAdmin ? "cursor-pointer hover:opacity-75" : ""}`}
                    title={
                      isSupportAdmin ? "Click to change task type" : undefined
                    }
                    onClick={() => isSupportAdmin && setIsEditingTaskType(true)}
                  >
                    {TASK_TYPE_LABELS[selectedTask.task_type] ||
                      selectedTask.task_type}
                    {isSupportAdmin && (
                      <svg
                        className="inline ml-1 w-2.5 h-2.5 opacity-50"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    )}
                  </span>
                )}

                {/* Priority editor — staff+admin can change. Show current
                    value with color badge; on click toggles to dropdown.
                    Audit AI 2026-05-23 wants detail-page priority change
                    in the v1 ship. */}
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-gray-500">Priority:</span>
                  <select
                    value={selectedTask.priority ?? "medium"}
                    onChange={async (e) => {
                      const newPriority = e.target.value as
                        | "low"
                        | "medium"
                        | "high"
                        | "urgent";
                      try {
                        await updateTaskPriority(selectedTask.id, newPriority);
                        queryClient.invalidateQueries({
                          queryKey: ["support-tasks"],
                        });
                        setSelectedTask({
                          ...selectedTask,
                          priority: newPriority,
                        });
                        showToast("success", `Priority set to ${newPriority}`);
                      } catch (err) {
                        showToast(
                          "error",
                          taskWriteErrorMessage(
                            err,
                            "Failed to update priority",
                          ),
                        );
                      }
                    }}
                    className={`text-xs px-2 py-0.5 rounded-md border font-medium cursor-pointer ${
                      TASK_PRIORITY_CONFIG[selectedTask.priority ?? "medium"]
                        .bgColor
                    } ${
                      TASK_PRIORITY_CONFIG[selectedTask.priority ?? "medium"]
                        .color
                    }`}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Parent Task Chain Indicator */}
              {parentTask && (
                <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm">
                  <svg
                    className="w-4 h-4 text-indigo-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  <span className="text-indigo-700">
                    Follow-up to{" "}
                    <button
                      onClick={() => setSelectedTask(parentTask)}
                      className="font-semibold underline hover:text-indigo-900"
                    >
                      Task #{parentTask.task_number}
                    </button>
                  </span>
                </div>
              )}

              {/* Customer Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Customer Information
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {/* Show resolved name first — prefer joined contact over snapshot */}
                  <div>
                    <div className="text-xs text-gray-500">Name</div>
                    <div className="text-sm font-medium text-gray-900">
                      {getContactDisplayName(selectedTask) || (
                        <span className="italic text-gray-400">
                          No name on file
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Email — prefer joined contact over snapshot.
                      2026-06-02: render as link to the joined contact
                      (matching the Member ID pattern below). Falls
                      back to plain text when there's no joined
                      contact id (snapshot-only tasks for soft-deleted
                      contacts). state.fromTaskId preserves the
                      back-to-task navigation contract used by the
                      contact page. */}
                  {(() => {
                    const snapshot =
                      selectedTask.customer_snapshot as CustomerSnapshot;
                    const email =
                      selectedTask.contact?.email || snapshot?.email;
                    const linkedContactId = selectedTask.contact?.id;
                    return email ? (
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <div className="text-sm font-medium text-gray-900">
                          {linkedContactId ? (
                            <Link
                              to={`/contacts/${linkedContactId}`}
                              state={{ fromTaskId: selectedTask.id }}
                              className="text-primary-600 hover:text-primary-800 hover:underline"
                            >
                              {email}
                            </Link>
                          ) : (
                            email
                          )}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  {/* Phone — prefer joined contact over snapshot */}
                  {(() => {
                    const snapshot =
                      selectedTask.customer_snapshot as CustomerSnapshot;
                    const phone =
                      selectedTask.contact?.phone_number || snapshot?.phone;
                    return phone ? (
                      <div>
                        <div className="text-xs text-gray-500">Phone</div>
                        <div className="text-sm font-medium text-gray-900">
                          {phone}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  {/* Member ID — render as link to the matching contact
                      (resolved via contacts.membership_id, which the Zuora
                      webhook populates from MemberNumber__c or
                      SecondaryMemberNumber__c). Falls back to plain text
                      if no contact has this membership_id. */}
                  {memberIdFromSnapshot && (
                    <div>
                      <div className="text-xs text-gray-500">Member Number</div>
                      <div className="text-sm font-medium text-gray-900">
                        {contactByMembershipId ? (
                          <Link
                            to={`/contacts/${contactByMembershipId.id}`}
                            state={{ fromTaskId: selectedTask.id }}
                            className="text-primary-600 hover:text-primary-800 hover:underline"
                          >
                            {memberIdFromSnapshot}
                          </Link>
                        ) : (
                          memberIdFromSnapshot
                        )}
                      </div>
                    </div>
                  )}
                  {/* Remaining snapshot fields */}
                  {Object.entries(
                    selectedTask.customer_snapshot as CustomerSnapshot,
                  ).map(([key, value]) => {
                    if (
                      key === "id" ||
                      key === "tags" ||
                      key === "name" ||
                      key === "email" ||
                      key === "phone" ||
                      key === "member_id" ||
                      value === undefined ||
                      value === null
                    ) {
                      return null;
                    }

                    const displayKey = key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase());

                    return (
                      <div key={key}>
                        <div className="text-xs text-gray-500">
                          {displayKey}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {String(value)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Customer Tags */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Customer Tags
                </h4>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(
                    selectedTask.customer_snapshot as CustomerSnapshot
                  ).tags?.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveCustomerTag(tag)}
                        className="ml-1 text-indigo-600 hover:text-indigo-800"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                  {(!(selectedTask.customer_snapshot as CustomerSnapshot)
                    .tags ||
                    ((selectedTask.customer_snapshot as CustomerSnapshot).tags
                      ?.length ?? 0) === 0) && (
                    <span className="text-xs text-gray-400">No tags yet</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCustomerTag}
                    onChange={(e) => setNewCustomerTag(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleAddCustomerTag()
                    }
                    placeholder="Add tag..."
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <button
                    onClick={handleAddCustomerTag}
                    disabled={!newCustomerTag.trim()}
                    className="px-2 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Task Details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Task Details
                </h4>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500">Due Date</div>
                    <div className="text-sm font-medium text-gray-900">
                      {selectedTask.due_date
                        ? formatDate(selectedTask.due_date)
                        : "—"}
                    </div>
                  </div>
                  {selectedTask.opened_at && (
                    <div>
                      <div className="text-xs text-gray-500">Opened At</div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatDate(selectedTask.opened_at)}
                      </div>
                    </div>
                  )}
                  {selectedTask.completed_at && (
                    <div>
                      <div className="text-xs text-gray-500">
                        {selectedTask.status === "cancelled"
                          ? "Cancelled At"
                          : "Completed At"}
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatDate(selectedTask.completed_at)}
                      </div>
                    </div>
                  )}
                  {selectedTask.source_invoice_number && (
                    <div>
                      <div className="text-xs text-gray-500">Invoice #</div>
                      <div className="text-sm font-medium text-gray-900">
                        {selectedTask.source_invoice_number}
                      </div>
                    </div>
                  )}
                  {selectedTask.notes && (
                    <div>
                      <div className="text-xs text-gray-500">Notes</div>
                      <div className="text-sm text-gray-900 bg-yellow-50 px-2 py-1.5 rounded mt-1 whitespace-pre-wrap">
                        {selectedTask.notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Escalation Status */}
              {selectedTask.escalated_to && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <svg
                      className="w-5 h-5 text-amber-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <span className="font-medium text-amber-800">
                      Escalated Task
                    </span>
                  </div>
                  {selectedTask.escalated_to_agent && (
                    <div className="text-sm text-amber-700 mb-1">
                      <span className="text-amber-600">Escalated to:</span>{" "}
                      {selectedTask.escalated_to_agent.name}
                    </div>
                  )}
                  {selectedTask.escalated_at && (
                    <div className="text-sm text-amber-700 mb-1">
                      <span className="text-amber-600">Escalated on:</span>{" "}
                      {formatDate(selectedTask.escalated_at)}
                    </div>
                  )}
                  {selectedTask.escalation_reason && (
                    <div className="text-sm text-amber-700">
                      <span className="text-amber-600">Reason:</span>{" "}
                      {selectedTask.escalation_reason}
                    </div>
                  )}
                </div>
              )}

              {/* Resolution Tags */}
              {selectedTask.tags && selectedTask.tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Resolution Tags
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTask.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedTask.notes && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Notes
                  </h4>
                  <p className="text-sm text-gray-700 bg-yellow-50 p-3 rounded-lg">
                    {selectedTask.notes}
                  </p>
                </div>
              )}

              {/* Activity Timeline - Always visible */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Activity Timeline
                    {linkedInteractions.length > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                        {linkedInteractions.length}
                      </span>
                    )}
                  </h4>
                  {linkedInteractions.length > 2 && (
                    <button
                      onClick={() =>
                        setActivityTimelineExpanded(!activityTimelineExpanded)
                      }
                      className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                    >
                      {activityTimelineExpanded
                        ? "Collapse"
                        : `Show all ${linkedInteractions.length}`}
                    </button>
                  )}
                </div>

                {linkedInteractions.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <svg
                      className="w-8 h-8 mx-auto text-gray-300 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">
                      No activity logged yet
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Click "Log Activity" to record interactions
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gray-200" />

                    <div className="space-y-3">
                      {(activityTimelineExpanded
                        ? linkedInteractions
                        : linkedInteractions.slice(0, 2)
                      ).map((interaction) => {
                        // Icon based on method/type
                        const getMethodIcon = (
                          method?: string,
                          type?: string,
                        ) => {
                          const m = method || type || "";
                          if (m.includes("phone") || m.includes("call")) {
                            return (
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                />
                              </svg>
                            );
                          }
                          if (m.includes("email")) {
                            return (
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                              </svg>
                            );
                          }
                          if (m.includes("meeting") || m.includes("person")) {
                            return (
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                              </svg>
                            );
                          }
                          return (
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                              />
                            </svg>
                          );
                        };

                        // Outcome color
                        const getOutcomeStyle = (outcome?: string) => {
                          if (!outcome) {
                            return "bg-gray-100 text-gray-600";
                          }
                          const o = outcome.toLowerCase();
                          if (
                            o.includes("success") ||
                            o.includes("complete") ||
                            o.includes("resolved")
                          ) {
                            return "bg-green-100 text-green-700";
                          }
                          if (
                            o.includes("no_answer") ||
                            o.includes("left_message")
                          ) {
                            return "bg-amber-100 text-amber-700";
                          }
                          if (o.includes("declined") || o.includes("cancel")) {
                            return "bg-red-100 text-red-700";
                          }
                          if (
                            o.includes("follow") ||
                            o.includes("callback") ||
                            o.includes("schedule")
                          ) {
                            return "bg-blue-100 text-blue-700";
                          }
                          return "bg-gray-100 text-gray-600";
                        };

                        const formatOutcome = (outcome?: string) => {
                          if (!outcome) {
                            return "";
                          }
                          return outcome
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase());
                        };

                        return (
                          <Link
                            key={interaction.id}
                            to={`/interactions?interaction=${interaction.id}`}
                            className="relative pl-8 block group"
                          >
                            {/* Timeline dot with icon */}
                            <div className="absolute left-0 w-6 h-6 bg-white border-2 border-blue-400 rounded-full flex items-center justify-center text-blue-500 group-hover:border-blue-600 group-hover:text-blue-600 transition-colors">
                              {getMethodIcon(
                                interaction.method,
                                interaction.type,
                              )}
                            </div>

                            <div className="bg-gray-50 rounded-lg p-3 hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900 capitalize">
                                      {(
                                        interaction.method ||
                                        interaction.type ||
                                        "Activity"
                                      ).replace(/_/g, " ")}
                                    </span>
                                    {interaction.outcome && (
                                      <span
                                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${getOutcomeStyle(interaction.outcome)}`}
                                      >
                                        {formatOutcome(interaction.outcome)}
                                      </span>
                                    )}
                                  </div>
                                  {interaction.description && (
                                    <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                                      {interaction.description}
                                    </p>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                                  {new Date(
                                    interaction.interaction_date,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </div>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>

                    {/* Show more indicator when collapsed */}
                    {!activityTimelineExpanded &&
                      linkedInteractions.length > 2 && (
                        <button
                          onClick={() => setActivityTimelineExpanded(true)}
                          className="relative pl-8 w-full mt-2 text-left"
                        >
                          <div className="absolute left-0 w-6 h-6 bg-gray-100 border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center text-gray-400">
                            <span className="text-xs font-bold">
                              +{linkedInteractions.length - 2}
                            </span>
                          </div>
                          <div className="py-2 text-sm text-primary-600 hover:text-primary-800 font-medium">
                            View {linkedInteractions.length - 2} more{" "}
                            {linkedInteractions.length - 2 === 1
                              ? "activity"
                              : "activities"}
                          </div>
                        </button>
                      )}
                  </div>
                )}
              </div>

              {/* Follow-up Tasks */}
              {childTasks.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                    Follow-up Tasks
                    <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">
                      {childTasks.length}
                    </span>
                  </h4>
                  <div className="space-y-2">
                    {childTasks.map((child) => (
                      <button
                        key={child.id}
                        onClick={() => setSelectedTask(child)}
                        className="w-full text-left p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-indigo-50 hover:border-indigo-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-gray-400">
                            #{child.task_number}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${TASK_STATUS_CONFIG[child.status].bgColor} ${TASK_STATUS_CONFIG[child.status].color}`}
                          >
                            {TASK_STATUS_CONFIG[child.status].label}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 font-medium">
                          {TASK_TYPE_LABELS[child.task_type] || child.task_type}
                        </div>
                        {child.due_date && (
                          <div className="text-xs text-gray-400 mt-1">
                            Due {new Date(child.due_date).toLocaleDateString()}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reassign Task (Admin only) */}
              {isSupportAdmin &&
                (selectedTask.status === "pending" ||
                  selectedTask.status === "in_progress") && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Reassign Task
                    </h4>
                    <div className="flex gap-2">
                      <select
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        onChange={(e) => {
                          if (e.target.value) {
                            handleSingleReassign(
                              selectedTask.id,
                              e.target.value,
                            );
                            e.target.value = "";
                          }
                        }}
                        defaultValue=""
                        disabled={reassignMutation.isPending}
                      >
                        <option value="">Select agent...</option>
                        {agents
                          .filter((a) => a.id !== selectedAgent?.id)
                          .map((agent) => (
                            <option key={agent.id} value={agent.id}>
                              {agent.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    {reassignMutation.isPending && (
                      <p className="text-xs text-gray-500 mt-1">
                        Reassigning...
                      </p>
                    )}
                  </div>
                )}

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 border-t border-gray-200">
                {/* Log Activity - single action button for pending/in_progress tasks */}
                {(selectedTask.status === "pending" ||
                  selectedTask.status === "in_progress") && (
                  <button
                    onClick={() => openResolutionModal(selectedTask)}
                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                  >
                    Log and Update
                  </button>
                )}

                {/* Create Follow-up Task */}
                <button
                  onClick={() => setFollowUpParentTask(selectedTask)}
                  className="w-full py-2 px-4 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg font-medium hover:bg-indigo-100 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Create Follow-up Task
                </button>

                {/* Escalation Actions */}
                {(selectedTask.status === "pending" ||
                  selectedTask.status === "in_progress") && (
                  <>
                    {/* Escalate button - show when task is not escalated */}
                    {/* For agents: only if they have a supervisor */}
                    {/* For admins: always show (error will display if agent has no supervisor) */}
                    {!selectedTask.escalated_to &&
                      (isSupportAdmin ||
                        currentAgentProfile?.supervisor_id) && (
                        <button
                          onClick={() => {
                            setTaskToEscalate(selectedTask);
                            setEscalationModalOpen(true);
                          }}
                          className="w-full py-2 px-4 bg-amber-100 text-amber-800 rounded-lg font-medium hover:bg-amber-200 transition-colors text-sm flex items-center justify-center gap-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 10l7-7m0 0l7 7m-7-7v18"
                            />
                          </svg>
                          Escalate to Supervisor
                        </button>
                      )}

                    {/* De-escalate button - show for admins or supervisors when task is escalated */}
                    {selectedTask.escalated_to &&
                      (isSupportAdmin ||
                        currentAgentProfile?.id ===
                          selectedTask.escalated_to) && (
                        <button
                          onClick={() =>
                            deescalateMutation.mutate(selectedTask.id)
                          }
                          disabled={deescalateMutation.isPending}
                          className="w-full py-2 px-4 bg-green-100 text-green-800 rounded-lg font-medium hover:bg-green-200 transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            />
                          </svg>
                          {deescalateMutation.isPending
                            ? "Returning..."
                            : "Return to Agent"}
                        </button>
                      )}
                  </>
                )}

                {(selectedTask.status === "completed" ||
                  selectedTask.status === "cancelled") && (
                  <button
                    onClick={() => handleReopenTask(selectedTask)}
                    className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
                  >
                    Reopen Task
                  </button>
                )}

                {/* External Action Buttons (disabled — not yet implemented) */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    disabled
                    className="py-2 px-4 bg-gray-100 text-gray-400 rounded-lg font-medium cursor-not-allowed text-sm"
                  >
                    Zero Zuora Balance
                  </button>
                  <button
                    disabled
                    className="py-2 px-4 bg-gray-100 text-gray-400 rounded-lg font-medium cursor-not-allowed text-sm"
                  >
                    Make a Call
                  </button>
                </div>

                {/* Share Button */}
                <button
                  onClick={() => {
                    const taskUrl = `${window.location.origin}/support-tasks?task=${selectedTask.id}`;
                    navigator.clipboard.writeText(taskUrl);
                    showToast("success", "Link copied");
                  }}
                  className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    />
                  </svg>
                  Get Share Link
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resolution Modal */}
      {resolutionModalOpen && taskToResolve && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
              onClick={() => setResolutionModalOpen(false)}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Log Activity
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Log an activity for{" "}
                <span className="font-medium">
                  {getContactDisplayName(taskToResolve) ||
                    (taskToResolve.customer_snapshot as CustomerSnapshot)
                      ?.email ||
                    "Unknown"}
                </span>
              </p>

              {/* Current Status + Change Status */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Current Status
                  </span>
                  <span
                    className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${TASK_STATUS_CONFIG[taskToResolve.status]?.color || "bg-gray-100 text-gray-700"}`}
                  >
                    {TASK_STATUS_CONFIG[taskToResolve.status]?.label ||
                      taskToResolve.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Change Status
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) =>
                      setNewStatus(e.target.value as TaskStatus | "")
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">(No change)</option>
                    {taskToResolve.status === "pending" &&
                      !isEscalationSupervisorOnly(taskToResolve) && (
                        <option value="in_progress">In Progress</option>
                      )}
                    <option value="completed">Completed</option>
                    {!isEscalationSupervisorOnly(taskToResolve) && (
                      <option value="cancelled">Cancelled</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Due Date (optional, independent of status). Hidden for the escalation
                  supervisor: migration 200 does not let them change it. */}
              {!isEscalationSupervisorOnly(taskToResolve) && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date (optional)
                  </label>
                  <input
                    type="date"
                    value={newDueDate}
                    onChange={(e) => setNewDueDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              )}

              {/* Quick Templates */}
              {resolutionTemplates.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quick Templates
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {resolutionTemplates.map((template) => {
                      const isSelected = selectedTemplateId === template.id;
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => {
                            setSelectedTemplateId(template.id);
                            setResolutionNotes(template.content);
                            setResolutionTags(template.suggested_tags || []);
                            // Auto-set status for resolution-type templates
                            if (COMPLETING_TEMPLATES.includes(template.name)) {
                              setNewStatus("completed");
                            }
                            setLogInteraction(true);
                          }}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-colors border ${
                            isSelected
                              ? "bg-blue-600 text-white border-blue-600 ring-2 ring-blue-300"
                              : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                          }`}
                        >
                          {template.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tags */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {RESOLUTION_TAGS.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleResolutionTag(tag)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        resolutionTags.includes(tag)
                          ? "bg-primary-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes{" "}
                  {newStatus === "completed" ? "(required)" : "(optional)"}
                </label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    newStatus === "completed" && !resolutionNotes.trim()
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  placeholder="Add any notes about this activity..."
                />
                {newStatus === "completed" && !resolutionNotes.trim() && (
                  <p className="text-xs text-red-500 mt-1">
                    Notes are required when marking a task as complete
                  </p>
                )}
              </div>

              {/* Log Interaction Checkbox */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={logInteraction}
                    onChange={(e) => setLogInteraction(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Log interaction with customer
                  </span>
                </label>
              </div>

              {/* Interaction Details (shown when checkbox is checked) */}
              {logInteraction && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Method
                    </label>
                    <select
                      value={interactionMethod}
                      onChange={(e) =>
                        setInteractionMethod(
                          e.target.value as InteractionMethod,
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="phone_call">Phone Call</option>
                      <option value="email">Email</option>
                      <option value="in_person">In Person</option>
                      <option value="meeting">Meeting</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Outcome
                    </label>
                    <select
                      value={interactionOutcome}
                      onChange={(e) =>
                        setInteractionOutcome(
                          e.target.value as InteractionOutcome | "",
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select outcome...</option>
                      <option value="successful">Successful</option>
                      <option value="no_answer">No Answer</option>
                      <option value="left_message">Left Message</option>
                      <option value="follow_up_needed">Follow-up Needed</option>
                      <option value="callback_scheduled">
                        Callback Scheduled
                      </option>
                      <option value="completed">Completed</option>
                      <option value="declined_offer">Declined Offer</option>
                      <option value="declined_resolution">
                        Declined Resolution
                      </option>
                    </select>
                  </div>

                  {/* Follow-up Date (shown for follow-up/callback outcomes) */}
                  {(interactionOutcome === "follow_up_needed" ||
                    interactionOutcome === "callback_scheduled") && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Follow-up Date
                      </label>
                      <input
                        type="date"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setResolutionModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Go Back
                </button>
                <button
                  type="button"
                  onClick={handleSubmitResolution}
                  disabled={
                    statusMutation.isPending ||
                    (newStatus === "completed" && !resolutionNotes.trim())
                  }
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                    newStatus === "completed"
                      ? "bg-green-600 hover:bg-green-700"
                      : newStatus === "cancelled"
                        ? "bg-gray-600 hover:bg-gray-700"
                        : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {statusMutation.isPending
                    ? "Saving..."
                    : newStatus === "completed"
                      ? "Complete Task"
                      : newStatus === "cancelled"
                        ? "Cancel Task"
                        : newStatus === "in_progress"
                          ? "Move to In Progress"
                          : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={createTaskModalOpen}
        onClose={() => setCreateTaskModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["support-agents"] });
          queryClient.invalidateQueries({ queryKey: ["agent-tasks"] });
        }}
      />

      {/* Create Follow-up Task Modal */}
      <CreateTaskModal
        isOpen={!!followUpParentTask}
        onClose={() => setFollowUpParentTask(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ["child-tasks", followUpParentTask?.id],
          });
          queryClient.invalidateQueries({ queryKey: ["support-agents"] });
          queryClient.invalidateQueries({ queryKey: ["agent-tasks"] });
          setFollowUpParentTask(null);
        }}
        parentTaskId={followUpParentTask?.id}
        parentTaskNumber={followUpParentTask?.task_number}
        prefilledContact={followUpParentTask?.contact as Contact | undefined}
      />

      {/* Agent Management Panel (Overlay).
          Wide layout so the Actions column (Edit Name / Edit
          Supervisor / Delete) doesn't get clipped under the
          default modal width on common laptop displays. */}
      {showAgentManagement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-[1400px] max-h-[90vh] overflow-y-auto">
            <AgentManagementPanel
              onClose={() => {
                setShowAgentManagement(false);
                // Clear the seed so the next "Manage Agents" click opens the list
                // rather than re-opening whichever agent was viewed last.
                setAgentPanelInitialId(null);
              }}
              isSupportAdmin={isSupportAdmin}
              initialAgentId={agentPanelInitialId}
            />
          </div>
        </div>
      )}

      {/* Escalation Modal */}
      {escalationModalOpen && taskToEscalate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Escalate Task
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Escalate this task to your supervisor for further assistance.
            </p>

            {/* Task Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-900">
                {getContactDisplayName(taskToEscalate) ||
                  (taskToEscalate.customer_snapshot as CustomerSnapshot)
                    ?.email ||
                  "Unknown"}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {TASK_TYPE_LABELS[taskToEscalate.task_type] ||
                  taskToEscalate.task_type}
              </div>
            </div>

            {/* Reason */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Escalation
              </label>
              <textarea
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Describe why this task needs to be escalated..."
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setEscalationModalOpen(false);
                  setTaskToEscalate(null);
                  setEscalationReason("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (taskToEscalate && escalationReason.trim()) {
                    escalateMutation.mutate({
                      taskId: taskToEscalate.id,
                      reason: escalationReason.trim(),
                    });
                  }
                }}
                disabled={
                  !escalationReason.trim() || escalateMutation.isPending
                }
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {escalateMutation.isPending ? "Escalating..." : "Escalate"}
              </button>
            </div>

            {/* Error display */}
            {escalateMutation.isError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {escalateMutation.error instanceof Error
                  ? escalateMutation.error.message
                  : "Failed to escalate task"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTasks;
